/**
 * PrivateRouter - Authenticated Routes Handler
 *
 * INITIALIZATION PATTERN:
 * -----------------------
 * This file handles USER-SPECIFIC initialization that:
 * - Runs after user authentication is confirmed
 * - Depends on currentUser being available
 * - Only applies to authenticated users
 *
 * Initializations in this file:
 * - Cross-tab broadcast listener (multi-tab session refresh)
 * - Firestore user document listener (session_version, filter_version, logout_version)
 * - PostHog user identification (posthog.identify)
 * - Sentry user context (Sentry.setUser)
 * - Userflow user identification
 * - Google Analytics user properties
 * - window.loop global object for debugging
 *
 * For GLOBAL INFRASTRUCTURE initializations (that don't need auth):
 * - See: src/App.tsx
 */
import * as Sentry from '@sentry/react'
import { doc, onSnapshot } from 'firebase/firestore'
import posthog from 'posthog-js'
import React, { lazy, useCallback, useEffect, useMemo, useState } from 'react'
import { matchPath, Navigate, RouteObject, useLocation, useRoutes } from 'react-router-dom'
import Loader from 'src/components/mui/Loader'
import { useAuth } from 'src/context/AuthContext'
import { useFilter } from 'src/context/FilterContext'
import { usePrivateLayoutDrawerContext } from 'src/context/PrivateLayoutDrawerContext'
import { usePublicAuthContext } from 'src/context/PublicAuthContext'
import { WizardType } from 'src/context/WizardContext'
import NotFound from 'src/pages/public/notFound/NotFound'
import { fetchUserOrgAndRole, firebaseDb, getWizardRoutes } from 'src/services/firebase-compat'
import { sendSlackNotification } from 'src/utils/api'
import { AccessLevelEnum, APP_NAME, APP_VERSION } from 'src/utils/config/config'
import { capitalize, get } from 'src/utils/config/lodashUtils'
import { isInternalUser } from 'src/utils/functions'
import { getAccessLevel } from 'src/utils/functions/accessLevel'
import { getSession, setSession } from 'src/utils/functions/localStorage'
import { markIntentionalReload, reportReloadBlocked } from 'src/utils/reloadLoopTracker'
import {
  initCrossTabBroadcast,
  forceReloadAllTabs,
  forceReloadAllTabsForFilter,
  logoutAllTabs,
  handleForceReloadMessage
} from 'src/utils/crossTabBroadcast'
import userflow from 'userflow.js'
import { PrivateLayout } from '../../layout'
import { balanceRoutes } from './balancePrivateRouter'
import { baseRoutes } from './basePrivateRouter'
import { coreRoutes } from './corePrivateRouter'
import { generalRoutes } from './generalRoutes'
import { guardRoutes } from './guardPrivateRouter'
import { onboardRoutes } from './onboardPrivateRouter'
import { quantRoutes } from './quantPrivateRouter'
import { recoverRoutes } from './recoverPrivateRouter'
import { reEngageRoutes } from './reEngagePrivateRouter'
import { truRoiRoutes } from './truRoiPrivateRouter'
// Extend Window interface with custom properties
declare global {
  interface Window {
    dataLayer?: any[]
    loop?: {
      user: any
      meta: {
        name: string
        version: string
      }
    }
  }
}

// Lazy-loaded components
const HomePage = lazy(() => import('src/pages/members/HomePage/HomePage'))
const NotificationCenter = lazy(() => import('src/pages/members/Notifications/NotificationCenter'))
const MagicLinkExpired = lazy(() => import('src/pages/public/Login/MagicLinkExpired'))
const LinkAuthProvider = lazy(() => import('src/pages/members/LinkAuthProvider/LinkAuthProvider'))
const DataRefreshStatus = lazy(() => import('src/pages/members/DataRefreshStatus'))
const Debugger = lazy(() => import('src/pages/members/Testing/Test'))
const SlackOAuth = lazy(() => import('src/pages/members/SlackOAuth/SlackOAuth'))
const UserProfile = lazy(() => import('src/pages/members/User/UserProfile/UserProfile'))
const UserManagement = lazy(() => import('src/pages/members/User/Mangement/UserManagement'))
const OktaSettings = lazy(() => import('src/pages/members/User/OrgSettings/OktaSettings'))
const Wizard = lazy(() => import('src/pages/members/Wizard/Wizard'))
const Integrations = lazy(() => import('src/pages/members/Integrations/Integrations'))
const OnboardUberEats = lazy(() => import('src/pages/members/Integrations/OnboardUberEats'))

// Use standard React Router useRoutes

const PrivateRouter = React.memo(() => {
  const location = useLocation()
  useLocation()
  const { refreshFilters } = useFilter()
  const {
    currentUser,
    orgConfig,
    globalRoutes,
    getApiData,
    logout,
    hasMagicLink,
    setStandaloneRoutesLoaded,
    getRoutes,
    getWizards,
    isOrgInActive
  } = useAuth()
  const { setPrivateRoutes, routes } = usePrivateLayoutDrawerContext()
  const { magicLinkFromAdmin } = usePublicAuthContext()

  const [filteredRouteList, setFilteredRouteList] = useState<RouteObject[]>([])
  const [wizardRoutes, setWizardRoutes] = useState<WizardType[]>([])

  // =========== Memoized Values ===========
  const navigateLink = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const redirectLink = params.get('redirect')

    if ((currentUser && get(currentUser, 'stage') === 'waiting' && !hasMagicLink) || isOrgInActive) {
      return '/create-account/wait'
    }
    return redirectLink || 'home'
  }, [location.search, currentUser, hasMagicLink, isOrgInActive])

  const flatRoute = useMemo(() => {
    const flatRoutes = routes
      .filter((e) => !!e)
      .map((route) => {
        const arr: string[] = []
        if ('subNav' in route && Array.isArray(route['subNav']) && route['subNav'].length > 0) {
          let subArr: string[] = route['subNav']
            .filter((subNav) => 'key' in subNav && typeof subNav.key === 'string')
            .map((subNav) => (subNav.key[0] === '/' ? subNav.key.slice(1) : `${route.key}/${subNav.key}`))
          arr.push(...subArr)
        } else {
          arr.push(route.key)
        }
        return arr
      })
      .reduce((prev, curr) => [...prev, ...curr], [])
    return flatRoutes
  }, [routes])

  const flattenPrivateRoutes = useCallback((routes: RouteObject[]) => {
    return routes.reduce((acc, route) => {
      if (!route.children) {
        return [...acc, route]
      }

      const indexRoute = route.children.find((child) => child.index)
      if (indexRoute) {
        acc.push({
          path: route.path,
          element: indexRoute.element
        })
      }

      const childrenWithBasePath = route.children
        .filter((child) => !child.index)
        .map((child) => ({
          ...child,
          path: child.path ? `${route.path}/${child.path}` : route.path
        }))

      acc.push(...flattenPrivateRoutes(childrenWithBasePath))
      return acc
    }, [] as RouteObject[])
  }, [])

  const privateRouteList: RouteObject[] = useMemo(
    () => [
      ...flattenPrivateRoutes(baseRoutes),
      ...flattenPrivateRoutes(guardRoutes),
      ...flattenPrivateRoutes(recoverRoutes),
      ...flattenPrivateRoutes(balanceRoutes),
      ...flattenPrivateRoutes(onboardRoutes),
      ...flattenPrivateRoutes(quantRoutes),
      ...flattenPrivateRoutes(truRoiRoutes),
      ...flattenPrivateRoutes(reEngageRoutes)
    ],
    []
  )

  const flatGlobalRoutes = useMemo(() => {
    return globalRoutes.reduce(
      (acc, route) => {
        if (route.subNav) {
          route.subNav.forEach((subRoute) => {
            if (subRoute.key) {
              if (subRoute.key[0] === '/') {
                acc.push({ key: subRoute.key, uid: route.uid })
              } else {
                const tempKey = `${route.key}/${subRoute.key}`
                const key = tempKey?.startsWith('/') ? tempKey : '/' + tempKey
                acc.push({ key, uid: route.uid })
              }
            }
          })
        } else {
          const key = route.key?.startsWith('/') ? route.key : '/' + route.key
          acc.push({ key, uid: route.uid })
        }

        return acc
      },
      [] as { key: string; uid: string }[]
    )
  }, [globalRoutes])

  const noAccess = useMemo(() => {
    if (currentUser && orgConfig) {
      try {
        const accessLevel = getAccessLevel(currentUser)
        const compareRoutes = getRoutes(accessLevel)
          .map((route) => {
            const routeUid = typeof route === 'string' ? route : route.uid
            const routeObj = globalRoutes.find((e) => e.uid === routeUid)
            return routeObj?.key
          })
          .filter((e) => !!e)

        if (location.pathname === '/' || !compareRoutes.includes(location.pathname)) {
          return false
        }
      } catch (error) {
        console.error("Error in PrivateRouter's noAccess", error)
      }
      return false
    }
    return true
  }, [currentUser, orgConfig, flatGlobalRoutes, globalRoutes, location.pathname])

  const privateRoutes = useMemo(() => {
    return [
      {
        path: '/',
        element: <PrivateLayout noAccess={noAccess} />,
        children: [
          ...filteredRouteList,
          { path: 'wizard/:wizardID', element: <Wizard /> },
          { index: true, element: <Navigate to={navigateLink} /> },
          { path: 'home', element: <HomePage /> },
          {
            path: 'dashboard',
            element: (
              <Navigate
                replace
                to="/home"
              />
            )
          },
          { path: 'data-refresh-status', element: <DataRefreshStatus /> },
          { path: 'link-phone', element: <LinkAuthProvider /> },
          { path: 'debugger', element: <Debugger /> },
          { path: 'connect/slack', element: <SlackOAuth /> },
          { path: 'user/profile', element: <UserProfile /> },
          { path: 'user/management', element: <UserManagement /> },
          { path: 'settings/okta', element: <OktaSettings /> },
          {
            path: 'notification-settings',
            element: <NotificationCenter />
          },
          { path: 'integrations', element: <Integrations /> },
          { path: 'onboard/uber', element: <OnboardUberEats /> },
          {
            path: 'access-denied',
            element: (
              <NotFound
                to={navigateLink}
                blockedOnDemo
              />
            )
          },
          {
            path: 'login/expired',
            element: (
              <>
                <MagicLinkExpired />
              </>
            )
          },
          {
            path: '*',
            element: <NotFound to={navigateLink} />
          }
        ]
      },
      ...generalRoutes,
      ...coreRoutes,
      {
        path: 'access-denied',
        element: (
          <NotFound
            to={navigateLink}
            blockedOnDemo
          />
        )
      },
      {
        path: '*',
        element: <NotFound to={navigateLink} />
      }
    ]
  }, [generalRoutes, filteredRouteList])

  // =========== Helper Functions ===========
  const isRouteExist = (route: string, flatRoutes: string[]) => {
    const path = flatRoutes.find((e) => {
      const match = matchPath({ path: route, caseSensitive: false }, `/${e}`)
      return match
    })
    return !!path
  }

  // =========== Effect Hooks ===========

  // Initialize cross-tab broadcast listener for multi-tab session refresh
  // This ensures ALL tabs reload when admin triggers session refresh
  useEffect(() => {
    const cleanup = initCrossTabBroadcast(handleForceReloadMessage)
    return cleanup
  }, [])

  useEffect(() => {
    let unsubscribe
    if (currentUser) {
      const query = doc(firebaseDb, `users/${currentUser.uid}`)

      unsubscribe = onSnapshot(query, async (snapShot) => {
        if (!snapShot.data()) {
          await new Promise((resolve) => setTimeout(resolve, 1000))
          if (!snapShot.data()) {
            logout()
            return
          }
        }

        const tempUser = getSession('data')

        if (tempUser) {
          const localSessionVersion = get(tempUser, 'session_version', 0)
          const localFilterVersion = get(tempUser, 'filter_version', 0)
          const localLogoutVersion = get(tempUser, 'logout_version', 0)
          const tempCurrentUser = snapShot.data()
          const sessionVersion = get(tempCurrentUser, 'session_version', 0)
          const filterVersion = get(tempCurrentUser, 'filter_version', 0)
          const logoutVersion = get(tempCurrentUser, 'logout_version', 0)

          // IMPORTANT: Process logout_version FIRST - it's the highest priority
          // If admin triggers logout alongside session/filter refresh, logout must win
          if (logoutVersion.toString() !== localLogoutVersion.toString()) {
            // logout user - this takes priority over session refresh
            try {
              sendSlackNotification({
                message: `USER LOGGED OUT FROM ADMIN (all tabs): for ${currentUser.email} of ${currentUser.org};`,
                channel: 'fe-logs',
                title: `USER LOGGED OUT FROM ADMIN (all tabs)`
              })
              // Broadcast logout to ALL tabs before logging out this tab
              logoutAllTabs('admin_logout', currentUser.uid)
              logout()
              return // Exit early - no need to process other updates after logout
            } catch (error) {
              console.error('Failed to logout user:', error)
            }
          }

          // Process filter_version BEFORE session refresh reload
          // This ensures filter updates aren't skipped when session also changes
          if (filterVersion.toString() !== localFilterVersion.toString()) {
            // refresh filter - reload ALL tabs to ensure filter consistency
            try {
              // Update sessionStorage BEFORE reload so other tabs have the new version
              setSession('data', tempCurrentUser)

              sendSlackNotification({
                message: `FILTER REFRESHED FROM ADMIN (all tabs): for ${currentUser.email} of ${currentUser.org};`,
                channel: 'fe-logs',
                title: `FILTER REFRESHED FROM ADMIN (all tabs)`
              })
              // Force reload ALL tabs to ensure fresh filters across all tabs
              markIntentionalReload('filter_version_mismatch')
              forceReloadAllTabsForFilter('admin_filter_refresh', currentUser.uid)
              return // Exit early - reload will happen
            } catch (error) {
              console.error('Failed to refresh filter:', error)
            }
          }

          // Process session_version LAST since it triggers a reload
          if (sessionVersion.toString() !== localSessionVersion.toString()) {
            // refresh session
            try {
              const data = await fetchUserOrgAndRole(currentUser.uid)
              await getApiData(currentUser, data)

              // Update sessionStorage BEFORE reload - check if it succeeded
              const setResult = setSession('data', tempCurrentUser)
              const afterSet = getSession('data')
              const setSucceeded = setResult && afterSet && get(afterSet, 'session_version', null) === sessionVersion

              if (!setSucceeded) {
                // setSession failed - don't reload (would cause infinite loop)
                reportReloadBlocked('setSession_failed')
                return
              }

              markIntentionalReload('session_version_mismatch')
              sendSlackNotification({
                message: `SESSION REFRESHED FROM ADMIN (all tabs): for ${currentUser.email} of ${currentUser.org};`,
                channel: 'fe-logs',
                title: `SESSION REFRESHED FROM ADMIN (all tabs)`
              })
              // Force reload ALL tabs to ensure fresh application chunks
              forceReloadAllTabs('admin_session_refresh', currentUser.uid)
              return
            } catch (error) {
              setSession('data', tempCurrentUser)
            }
          }

          // Update sessionStorage AFTER all comparisons are done
          setSession('data', tempCurrentUser)
        }
      })
    }

    return () => {
      unsubscribe && unsubscribe()
    }
  }, [currentUser])

  useEffect(() => {
    setPrivateRoutes(filteredRouteList)
  }, [filteredRouteList])

  useEffect(() => {
    if (!orgConfig || !currentUser) return

    async function getWizardRoute() {
      const accessLevel = getAccessLevel(currentUser)
      const findAccessLevel = Object.keys(get(orgConfig, 'accessLevels', {}))
      const tempAccessLevel =
        findAccessLevel.includes(accessLevel) && !findAccessLevel.includes(AccessLevelEnum.DEMO) ? accessLevel : AccessLevelEnum.BUSINESS_ADMIN
      const wizardList = getWizards(tempAccessLevel)
      const wizardRoutes = await getWizardRoutes(wizardList)
      setWizardRoutes(wizardRoutes)
    }

    getWizardRoute()
  }, [currentUser, orgConfig])

  useEffect(() => {
    if (!orgConfig || !currentUser || !flatRoute || !wizardRoutes) return

    const formatRoutes = () => {
      try {
        const temporaryRouteList = privateRouteList.filter(
          (item) => currentUser?.internal || item.path === '/login/expired' || isRouteExist(item.path, flatRoute)
        )

        const wizardRoutesFiltered = wizardRoutes.reduce((acc, item) => {
          const stepRoutes = item.steps
            .map((step) => privateRouteList.find((route) => matchPath({ path: `${step.key}`, caseSensitive: false }, `/${route.path}`)))
            .filter(Boolean) as RouteObject[]
          return [...acc, ...stepRoutes]
        }, [] as RouteObject[])

        setFilteredRouteList((prev) => {
          const set = new Set(prev)
          temporaryRouteList.forEach((route) => set.add(route))
          wizardRoutesFiltered.forEach((route) => set.add(route))
          return Array.from(set)
        })

        setStandaloneRoutesLoaded(true)
      } catch (error) {
        console.error('Error in PrivateRouter formatRoute', error)
      }
    }

    formatRoutes()
  }, [orgConfig, currentUser, flatRoute, wizardRoutes, privateRouteList])

  useEffect(() => {
    if (currentUser?.email) {
      window.dataLayer = window.dataLayer || []
      const { uid, email, phoneNumber, org, 'access-level': accessLevel, name: fullName, photoURL: avatar } = currentUser

      const user = {
        id: uid,
        signup_date: Date.now() / 1000,
        userId: uid,
        fullName: capitalize(fullName),
        email,
        phoneNumber,
        company: org,
        accessLevel,
        avatar
      }

      window?.dataLayer?.push(user)
      window.loop = {
        user,
        meta: {
          name: APP_NAME,
          version: APP_VERSION
        }
      }

      const identityProperties = {
        ...window?.loop?.meta,
        ...window?.loop?.user
      }

      // Updated PostHog API calls with Group Analytics for B2B
      posthog.identify(user.email, identityProperties)

      // PostHog Group Analytics - critical for B2B metrics
      // This enables company-level analytics, not just user-level
      // @ts-ignore - group method exists but types may be incomplete
      posthog.group('company', user.company, {
        name: user.company,
        org_id: currentUser?.org,
        access_level: user.accessLevel,
        app_version: APP_VERSION
      })

      posthog.capture('$pageview', identityProperties)

      // Add company and email as properties to the user
      const properties = {
        company: user.company,
        email: user.email,
        ...window?.loop?.meta,
        ...window?.loop?.user
      }

      // Capture a company identified event with all properties
      posthog.capture('company_identified', properties)

      // Enhanced Sentry User Context - rich context for filtering and RCA
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.fullName
      })
      Sentry.setTag('organization', user.company)
      Sentry.setTag('accessLevel', user.accessLevel)
      Sentry.setContext('user', {
        id: user.id,
        email: user.email,
        fullName: user.fullName,
        org: user.company,
        accessLevel: user.accessLevel,
        appVersion: APP_VERSION
      })

      // Identify the user in Userflow
      if (!(magicLinkFromAdmin && !isInternalUser(currentUser))) {
        const userflowProperties = {
          user_id: user.id,
          name: user.fullName,
          email: user.email,
          company: user.company,
          access_level: user.accessLevel,
          signup_date: Date.now() / 1000
        }

        userflow.identify(user.id, userflowProperties)
      }
    }
  }, [currentUser, location.pathname, orgConfig, magicLinkFromAdmin])

  const newRoutes = useRoutes(privateRoutes)

  if (currentUser && currentUser.stage === 'waiting') {
    return <>{newRoutes}</>
  }

  return <>{filteredRouteList.length > 0 ? newRoutes : <Loader loading />}</>
})

export default PrivateRouter
